import { throttle } from "lodash-es"
import { LadiesMain } from "../../global/ladies-main"
import { parseQuery } from "../../util/parse-query"
import "../royal-slider"

const disableBodyScroll = (() => {
    /**
     * Private variables
     */
    let _selector = false
    let _element = false
    let _clientY

    /**
     * Polyfills for Element.matches and Element.closest
     */
    if (!Element.prototype.matches)
        Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector

    if (!Element.prototype.closest)
        Element.prototype.closest = function (s) {
            let ancestor = this
            if (typeof el === "undefined" || !document.documentElement.contains(el)) {
                return null
            }
            do {
                if (ancestor.matches(s)) {
                    return ancestor
                }
                ancestor = ancestor.parentElement
            } while (ancestor !== null)
            return el
        }

    /**
     * Prevent default unless within _selector
     *
     * @param  event object event
     * @return void
     */
    const preventBodyScroll = (event) => {
        if (false === _element || !event.target.closest(_selector)) {
            event.preventDefault()
        }
    }

    /**
     * Cache the clientY co-ordinates for
     * comparison
     *
     * @param  event object event
     * @return void
     */
    const captureClientY = (event) => {
        // only respond to a single touch
        if (event.targetTouches.length === 1) {
            _clientY = event.targetTouches[0].clientY
        }
    }

    /**
     * Detect whether the element is at the top
     * or the bottom of their scroll and prevent
     * the user from scrolling beyond
     *
     * @param  event object event
     * @return void
     */
    const preventOverscroll = (event) => {
        // only respond to a single touch
        if (event.targetTouches.length !== 1) {
            return
        }

        const clientY = event.targetTouches[0].clientY - _clientY

        // The element at the top of its scroll,
        // and the user scrolls down
        if (event.cancelable && _element.scrollTop === 0 && clientY > 0) {
            event.preventDefault()
        }

        // The element at the bottom of its scroll,
        // and the user scrolls up
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
        if (_element.scrollHeight - _element.scrollTop <= _element.clientHeight && clientY < 0 && event.cancelable) {
            event.preventDefault()
        }
    }

    /**
     * Disable body scroll. Scrolling with the selector is
     * allowed if a selector is porvided.
     *
     * @param  boolean allow
     * @param  string selector Selector to element to change scroll permission
     * @return void
     */
    return (allow, selector) => {
        if (typeof selector !== "undefined") {
            _selector = selector
            _element = document.querySelector(selector)
        }

        if (true === allow) {
            if (false !== _element) {
                _element.addEventListener("touchstart", captureClientY, false)
                _element.addEventListener("touchmove", preventOverscroll, false)
            }
            document.body.addEventListener("touchmove", preventBodyScroll, false)
        } else {
            if (false !== _element) {
                _element.removeEventListener("touchstart", captureClientY, false)
                _element.removeEventListener("touchmove", preventOverscroll, false)
            }
            document.body.removeEventListener("touchmove", preventBodyScroll, false)
        }
    }
})()

/**
 * Class Galerie
 * Die Galerie-Klasse
 *
 * Requirements:
 * jquery
 * jquery.mousewheel
 * jquery.throttle.debounce
 * royalSlider
 * flowplayer
 */
export function Galerie(config) {
    this.initConfig(config)
}

/**
 * Initialisiere die Konfiguration
 */
Galerie.prototype = {
    anzeigeCount: 0,

    initConfig: function (config) {
        this.config = config

        /**
         * Main Gallery-Container
         */
        if (this.config.domContainerId === undefined) this.config.domContainerId = "#gallery"

        if (this.config.domSliderThumbsTriggersId === undefined)
            this.config.domSliderThumbsTriggersId = ".gallery_item, .auftrag-main-thumb"

        if (this.config.domSliderlId === undefined)
            this.config.domSliderlId = `${this.config.domContainerId} .gallery-slider`

        if (this.config.domOverflow === undefined)
            this.config.domOverflow = `${this.config.domContainerId} .gallery-slider .rsOverflow`

        if (this.config.domCountContainerId === undefined)
            this.config.domCountContainerId = `${this.config.domContainerId} .imageCount`

        if (this.config.domPlayerId === undefined) this.config.domPlayerId = `${this.config.domContainerId} .flowplayer`

        if (this.config.domPlayerUi === undefined) this.config.domPlayerUi = `${this.config.domContainerId} .fp-ui`

        if (this.config.rsNavNext === undefined)
            this.config.rsNavNext = `${this.config.domContainerId} .royal-next-container`

        if (this.config.rsNavPrevious === undefined)
            this.config.rsNavPrevious = `${this.config.domContainerId} .royal-previous-container`

        if (this.config.thumbArrowLeft === undefined)
            this.config.thumbArrowLeft = `${this.config.domContainerId} .gallery-thumb-left`

        if (this.config.thumbArrowRight === undefined)
            this.config.thumbArrowRight = `${this.config.domContainerId} .gallery-thumb-right`

        if (this.config.rsNavCloseFullcreen === undefined)
            this.config.rsNavCloseFullcreen = `${this.config.domContainerId} .royal-close-container`

        if (this.config.rsNavMediaText === undefined)
            this.config.rsNavMediaText = `${this.config.domContainerId} .galleryText`

        if (this.config.rsNavMediaTextMobile === undefined)
            this.config.rsNavMediaTextMobile = `${this.config.domContainerId} .galleryTextMobile`

        if (this.config.videoContent === undefined) {
            this.config.videoContent = `${this.config.domContainerId} .videoContent`
        }

        if (this.config.isMobileVersion === undefined) {
            this.config.isMobileVersion = false
        }

        if (this.config.noBannerOnGalleryClosed === undefined) {
            this.config.noBannerOnGalleryClosed = false
        }

        if (this.config.domThumbs === undefined) {
            this.config.domThumbs = `${this.config.domContainerId} .rsThumbs`
        }

        if (this.config.royalSliderContainer === undefined)
            this.config.royalSliderContainer = `${this.config.domSliderlId} .rsContainer`

        if (this.config.royalSliderTransitionSpeed === undefined) this.config.royalSliderTransitionSpeed = 300

        if (this.config.isMobileDevice === undefined) this.config.isMobileDevice = false

        if (this.config.showMobileView === undefined) this.config.showMobileView = false

        if (this.config.hatVideos === undefined) this.config.hatVideos = false

        if (this.config.hatImages === undefined) this.config.hatImages = false

        if (this.config.ivwTag) this.ivwTag = this.config.ivwTag

        if (this.config.ivwDomain) this.ivwDomain = this.config.ivwDomain

        // Base Url
        this.baseUrl = window.location.href.split("#")[0]
        // Eine Flag um zu wissen, ob eine Flowplayer-Video gestartet wurde
        this.flowplayerVideoStarted = false
        // Eine Flag um zu wissen, ob der Flowplayer i Fullscreen-Modus sich befindet
        this.flowplayerIsFullscreen = false

        /**
         *
         * @type {jQuery}
         */
        this.sliderContainer = null

        /**
         * Instanz des RoyalSlider
         * @type {RoyalSlider|jQuery}
         */
        this.slider = null

        this.showThumbs = true

        this.actualScrollPosition = 0

        this.navigationVisible = true

        this.currentSlideId = 0

        /**
         * Aktuelle Displayhöhe
         *
         * @type {number}
         */
        this.deviceHeight = 0

        this.deviceWidth = 0

        /**
         * Wenn die Displayhöhe kleiner als dieser integer, werden Thumbs nicht angezeigt
         *
         * @type {number}
         */
        this.config.minHeightForThumbs = 960

        /**
         * Diese Konditionen definieren, ob es ein mobilen oder Desktop-Ansicht angezeigt wird
         *
         * @type {number}
         */
        this.config.minWidthForDestopView = 768
        this.config.minHeightForDesktopView = 500

        this.config.allowScrollingInSelector = this.config.domOverflow

        this.isIos = false
    },

    /**
     *
     */
    init: function () {
        if (!this.config.hatImages && !this.config.hatVideos) {
            window.console.info("Gallery: No media in configuration object")
            return
        }

        this.isIos = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)

        this.deviceHeight = this._getDeviceHeight()
        this.deviceWidth = this._getDeviceWidth()
        this._updateViewType()

        if (!this.initSlider()) {
            return
        }

        this.registerEvents()
        this.findGalleryIdForAid()
        this._reopenImageFromUrl()

        $(window).trigger("resize")
    },

    /**
     * Initializiere den Slider
     */
    initSlider: function () {
        /**
         * Init Royalslider
         *
         * http://dimsemenov.com/plugins/royal-slider/documentation/#options
         */
        let config = this.getConfig()

        config = this.registerThumbsConfig(config)
        this.sliderContainer = $(this.config.domSliderlId)

        if (!this.sliderContainer.length) {
            window.console.error(`Royal-Slider element mit dem Selektor = "${this.config.domSliderlId}" nicht gefunden`)
            return false
        }

        if ("royalSlider" in this.sliderContainer) {
            this.sliderContainer.royalSlider(config)
            this.slider = this.sliderContainer.data("royalSlider")
        }

        if (this.slider && "ev" in this.slider) {
            this.slider?.ev?.on(
                "rsEnterFullscreen",
                function () {
                    if (!("_hideGalleryThumbnailNavigation" in this)) {
                        return
                    }

                    this._hideGalleryThumbnailNavigation.bind(this)
                }.bind(this),
            )

            this.slider?.ev?.on("rsSlideClick", (rsEvent, clickEvent) => {
                const clickedElementBase = $(clickEvent.target).closest(".galleryText")

                if (!clickedElementBase.length) {
                    return true
                }

                rsEvent.stopPropagation()
                rsEvent.stopImmediatePropagation()

                clickEvent.stopPropagation()
                clickEvent.stopImmediatePropagation()

                return false
            })
        }

        $(document).on("keydown", throttle(this.handleArrowKeyNavigation.bind(this), 350))

        // Fix: Iphone 10 scrolling problem
        $(this.config.royalSliderContainer).on("touchmove touchend", () => {})

        if (this.config.showMobileView === true) {
            $(this.config.domSliderlId).addClass("mobile-slider")
        }

        return true
    },

    /**
     * Hole Slider-Konfiguration
     */
    getConfig: function () {
        return {
            fullscreen: {
                enabled: true,
                nativeFS: false,
                buttonFS: false,
            },
            loop: true,
            // imageScaleMode: 'fit-if-smaller',imageCountInner
            imageScaleMode: this.config.showMobileView === true ? "none" : "fit-if-smaller",
            navigateByClick: false, //probleme mit Videos -
            numImagesToPreload: 3,
            arrowsNav: false,
            arrowsNavAutoHide: false,
            arrowsNavHideOnTouch: false,
            keyboardNavEnabled: false,
            fadeinLoadedSlide: false,
            globalCaption: false,
            globalCaptionInside: false,
            autoScaleSlider: true,
            transitionSpeed: this.config.royalSliderTransitionSpeed,
        }
    },

    /**
     * Registriere die Konfiguration der Thumbs
     */
    registerThumbsConfig: function (config) {
        config.controlNavigation = "thumbnails"
        config.thumbs = {
            arrows: true,
            appendSpan: true,
            firstMargin: true,
            spacing: 15,
            arrowLeft: $(this.config.thumbArrowLeft),
            arrowRight: $(this.config.thumbArrowRight),
        }

        return config
    },

    /**
     * Registriere Slider-Events
     */
    registerEvents: function () {
        this._eventThumbClicked()

        this._eventEnterFullscreen()

        this._eventExitFullscreen()

        this._eventBeforeAnimationStart()

        this._eventAfterSlideChange()

        this._eventSlideClicked()

        this._eventVideoClicked()

        this._eventSlideScrolled()

        this._eventNavArrowClicked()

        this._eventNavCloseFullscreenClicked()

        this._eventWindowResized()

        this._registerVideoPlayers()
    },

    /**
     * @private
     * @param {flowplayer} flowPlayer
     */
    flowPlayerEvents: function (flowPlayer) {
        // merke Flowplayer in Fullscreen-Modus
        flowPlayer.bind(
            "fullscreenchange",
            ((flowPlayer) => {
                this.flowplayerIsFullscreen = flowPlayer.isFullscreen
            }).bind(this, flowPlayer),
        )

        // Video startet
        flowPlayer.bind("progress", (loadEvent) => {
            const playerElement = loadEvent.target
            const videoElement = playerElement.querySelector("video")

            // Flowplayer 5.4.4 triggert das erste progress-Event erst, wenn
            // 	das Video bereits einige Millisekunden abgespielt wurde.
            // 	Da sich das in zukünftigen Versionen ändern könnte hier Abfrage
            // 	nach currentTime.
            if (videoElement.currentTime > 0.0 && !playerElement.classList.contains("rsNoDrag")) {
                playerElement.classList.add("rsNoDrag")
            }
        })
    },

    /**
     * @private
     * @param {KeyboardEvent} keyEvent
     */
    handleArrowKeyNavigation: function (keyEvent) {
        if (!this.sliderContainer || !this.sliderContainer.data("galleryActive") || !this.slider) {
            return
        }

        switch (keyEvent.keyCode) {
            case 37: // ArrowLeft
                this.slider.prev()
                break

            case 39: // ArrowRight
                this.slider.next()
                break
        }
    },

    /**
     * @private
     */
    findGalleryIdForAid: () => {
        const galleryParam = parseQuery(location.hash.substring(1))
        let galleryKey = false

        if (!galleryParam.galleryAid) {
            return
        }

        const listItems = $("#telefonsex .card-list > div")
        $.each(listItems, (index, element) => {
            if (null !== element.querySelector(`[src*="/${galleryParam.galleryAid}-"]`)) {
                galleryKey = index
            }
        })

        if (false === galleryKey) {
            return
        }

        location.hash = "#gallery=".concat(galleryKey)
    },

    _updateViewType: function () {
        this.config.showMobileView = false

        if (
            this.deviceHeight < this.config.minHeightForDesktopView ||
            this.deviceWidth < this.config.minWidthForDestopView
        ) {
            this.config.showMobileView = true
        }
    },

    _getDeviceHeight: () => Math.max(document.documentElement.clientHeight, window.innerHeight || 0),

    _getDeviceWidth: () => Math.max(document.documentElement.clientWidth, window.innerWidth || 0),

    _eventAfterSlideChange: function () {
        this.slider?.ev?.on("rsAfterSlideChange", (galleryEvent) => {
            const videoElement = $(this.slider.currSlide.content[0]).find(".flowplayer")
            const slider = galleryEvent.currentTarget

            $(document).trigger("slideChanged:gallery.royal-slider", slider)

            const slideObject = slider.currSlide

            LadiesMain.nodesUpdated.notify({
                state: {
                    addedNodes: [slideObject.content.get(0)],
                    removedNodes: [],
                },
            })

            if (videoElement.length > 0) {
                // Verstecke das Thumb
                const currentFpApi = videoElement.data("flowplayer")
                currentFpApi.one("progress", (e, api) => {
                    $(e.currentTarget).css("background-image", "none")
                })

                // disable all Flowplayers
                const api = $(this.config.domPlayerId).data("flowplayer")

                if (api) {
                    // Aktiviere den Player wieder, wenn man wieder zur Video scrollt
                    api.disable(false)
                }
            }

            // Tracking
            this.track($(this.slider.currSlide.content[0]).hasClass("additional-banner"))

            $(document).trigger("gallery:reopened")
        })
    },

    /**
     * Gehe zum dem nächsten Slide wenn Maus-Scroll verwendet wird.
     */
    _eventSlideScrolled: function () {
        const os = this._getOS()

        // Disable Scrolling beim Macs
        if ("Mac OS" === os) {
            return
        }

        // TODO .mousewheel is not a function
        /*$(this.config.domSliderlId).mousewheel((event, delta, deltaX, deltaY) => {
            if (delta > 0) {
                this.slider.prev()
            } else if (deltaY < 0) {
                this.slider.next()
            }
            event.stopPropagation()
            event.preventDefault()
        })*/
    },

    /**
     * Gehe zum dem nächsten Slide wenn ein Bereich oder Slide gecklickt.
     * (Ausnahme - Video).
     * Wir verwenden ein Event dafür, da die navigateByClick-Option keine
     * Anpassungen für Videos ermöglicht.
     */
    _eventSlideClicked: function () {
        let videoUiClicked = false
        let disabledElementClicked = false
        let touchStartElement = null

        this.slider?.slider?.find(".rsContainer").on("mousedown", (mouseEvent) => {
            touchStartElement = $(mouseEvent.target)
        })

        this.slider?.ev?.on("rsSlideClick", (event, originalEvent) => {
            videoUiClicked = $(originalEvent.target).filter('[class^="fp-"], div[class*=" fp-"]')
            if (!videoUiClicked.length && null !== touchStartElement) {
                videoUiClicked = touchStartElement.filter('[class^="fp-"], div[class*=" fp-"]')
                touchStartElement = null
            }

            disabledElementClicked = $(originalEvent.target).hasClass("disable-gallery-events")
            if (!disabledElementClicked.length && null !== touchStartElement) {
                disabledElementClicked = touchStartElement.hasClass("disable-gallery-events")
                touchStartElement = null
            }

            // wenn es ein Video ist oder disabled gallery CSS_Klasse gesetzt ist - ignoriere
            if (!videoUiClicked.length && !disabledElementClicked) {
                // Close Fullscreen, wenn der Bild nicht angecklick wurde
                if (true !== $(originalEvent.target).hasClass("rsMainSlideImage")) {
                    if (this.config.isMobileDevice || this.config.showMobileView) {
                        event.target.exitFullscreen()
                    } else {
                        this._gallerySlideDirectional(originalEvent, event.target)
                    }
                } else {
                    event.target.next()
                }
            }
        })
    },

    /**
     *
     * @private
     * @param {Event} galleryEvent
     * @param {royalSlider} gallery
     */
    _gallerySlideDirectional: (galleryEvent, gallery) => {
        const sliderElement = gallery.slider
        const sliderMidpoint = sliderElement.width() / 2
        const horizontalPosition = galleryEvent.clientX

        if (horizontalPosition <= sliderMidpoint) {
            gallery.prev()
        } else {
            gallery.next()
        }
    },

    _eventVideoClicked: function () {
        $(document).on("click", this.config.domPlayerUi, (event) => {
            if (true === this.slider.dragSuccess) {
                this._pauseAllPlayers()

                // Deaktiviere den Player, wenn man scrollt - da wird auch ein Click-Event ausgelöst
                const api = $(this.config.domPlayerId).data("flowplayer")
                if (api) {
                    api.disable(true)
                }
            }
        })
    },

    /**
     * Verschiedene Anpassungen vor dem Anfang der Animation
     */
    _eventBeforeAnimationStart: function () {
        const _this = this

        this.slider?.ev?.on("rsBeforeAnimStart", function (event) {
            if (!event.target.currSlideId) {
                event.target.currSlideId = 0
            }

            // stop alle flowplayers
            _this._pauseAllPlayers()

            // Change Url
            _this._changeUrlHashdata(event.target.currSlideId)

            if (this.currSlide?.content?.data("caption")) {
                $(_this.config.domCountContainerId).html(this.currSlide.content.data("caption"))
            } else {
                // Elementzahl ändern

                // Berechne der Banner-Offset
                const bannersOnPositions = []
                let currentSlidePosition = event.target.currSlideId + 1

                for (let i = 0; i < _this.slider.slides.length; i++) {
                    if (true === $(_this.slider.slides[i].content[0]).hasClass("additional-banner")) {
                        bannersOnPositions.push(i)
                    }
                }

                /*
                Aktuell ünterstützen wir ein Banner am Ende der Galerie und ein Banner auf dem 2. Platz.
                Nach dem ersten Banenr mus der Anzahl runter gesetzt werden.
                 */
                if (bannersOnPositions.length > 0 && event.target.currSlideId > bannersOnPositions[0]) {
                    currentSlidePosition--
                }

                $(_this.config.domCountContainerId).html(
                    `<div class="imageCountInner">${currentSlidePosition} von ${_this.slider.numSlides - _this.anzeigeCount}${
                        this.currSlide.content.data("galleryText") !== ""
                            ? `<br/>${this.currSlide.content.data("galleryText")}`
                            : ""
                    }</div>`,
                )
            }

            _this._repositionVideo()
        })
    },

    /**
     * Stoppe alle Video Players
     */
    _pauseAllPlayers: function () {
        $(this.config.domPlayerId).each(function () {
            const fp_api = $(this).data("flowplayer")
            fp_api.pause()
        })
    },

    /**
     * Registriere rsEnterFullscreen Royalslider-Event
     */
    _eventEnterFullscreen: function () {
        const _this = this

        this.slider?.ev?.on("rsEnterFullscreen", function (event) {
            document.body.classList.add("gallery-open")

            if ($(".navbar-container").length > 0) {
                $(".navbar-container").hide()
            }

            _this._repositionVideo()

            _this._suppressEscapeButtonOnVideo(event)

            if (_this.slider.currSlideId === _this.currentSlideId) {
                _this.track($(_this.slider.currSlide.content[0]).hasClass("additional-banner"))
            }

            _this.actualScrollPosition = window.sessionStorage.getItem("einzelansichtPosition")

            if (this.currSlide.content.data("caption")) {
                $(_this.config.domCountContainerId).html(this.currSlide.content.data("caption"))
            }

            if (_this.showThumbs) {
                _this._domShowThumbNav()
            }

            $(window).trigger("resize")
        })
    },

    /**
     * Track Gallery
     *
     * @private
     */
    track: (isBanner) => {
        // if (Ladies.Utility.useTracking() && isBanner && this.ivwDomain) {
        //    Ladies.Ivw.track(this.ivwDomain, "ladies_galerie_banner")
        // }
        window.Tracking.trackGallery()
    },

    _repositionVideo: function () {
        if (this.config.showMobileView) {
            // positioniere das play-icon für mobile Geräte
            $(`${this.config.domContainerId} .icon-play-mobile`).css(
                "left",
                ($(`${this.config.domContainerId} .rsContainer`).width() - 50) / 2,
            )
            $(`${this.config.domContainerId} .icon-play-mobile`).css(
                "top",
                ($(`${this.config.domContainerId} img.mobile-video`).height() - 53) / 2,
            )
        }
    },

    /**
     * Nimmt die Kontrolle über ESC-Button, wenn FlowPlayer im Fullscreen-Modus.
     *
     * @param event rsEnterFullscreen Event von RoyalSlider
     */
    _suppressEscapeButtonOnVideo: function (event) {
        event.target._doc.off("keyup.rs0fullscreen")
        event.target._doc.off("keydownrs0")
        event.target._doc.on("keyup.rs0fullscreen", (e) => {
            if (e.keyCode !== 27) {
                return false
            }
            // get aktive Flowplayer
            const fp = $(this.config.domPlayerId).data("flowplayer")

            // disable ESC from royalslider when flowplayer in fullscreen modus
            if (event.target.currSlide.images === null && fp && this.flowplayerIsFullscreen) {
                e.stopImmediatePropagation()
                e.stopPropagation()
                e.preventDefault()
                this.flowplayerIsFullscreen = false

                return false
            }
            // Full screen-Modus verlassen

            event.target.exitFullscreen()
            if (fp) {
                fp.pause()
            }
        })
    },

    /**
     * Slider verstecken wenn das Fullscreen-Modus verlassen wird.
     */
    _eventExitFullscreen: function () {
        this.slider?.ev?.on("rsExitFullscreen", () => {
            document.body.classList.remove("gallery-open")

            if ($(".navbar-container").length > 0) {
                $(".navbar-container").show()
            }

            if (true === this.isIos) {
                disableBodyScroll(false, this.config.allowScrollingInSelector)
            }

            const scrollLocation = $("html, body").scrollTop() || window.pageYOffset

            this._pauseAllPlayers()
            $(this.config.domSliderGalleryTitleId).hide()
            $(this.config.domSliderlId).hide()
            this._domHideThumbNav()

            this.sliderContainer.data("galleryActive", false)
            $(document).data("keyOverrideActive", false)

            // Restore Scroll-Position
            if (!this.isIOSDevice() && "scrollRestoration" in history) {
                history.scrollRestoration = "manual"
            }

            LadiesMain.removeHashtagFromUrl(this.config.domContainerId.substring(1))
            window.scrollTo(0, scrollLocation)
        })
    },

    isIOSDevice: () =>
        ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
            navigator.platform,
        ) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document),

    /**
     * Zeige das Royal-Slider im Fullscreen-Modus, wenn man ein Bild oder Video klickt
     */
    _eventThumbClicked: function () {
        const _this = this

        $(this.config.domSliderThumbsTriggersId).click(function (e) {
            e.preventDefault()
            if (!_this.slider.isFullscreen) {
                // schalte die Transition aus
                _this.slider.st.transitionSpeed = 0

                // gehe zum Slide X
                _this.currentSlideId = _this._getCurrentSlideId(this)

                _this.openSlider()

                _this.slider.goTo(_this.currentSlideId)

                // Elementenzahl hinzufügen
                _this._domCreateCountContainer()

                _this._domCreateNavContainer()

                // schalte die Transition wieder ein für nächste Slides
                setTimeout(() => {
                    _this.slider.st.transitionSpeed = _this.config.royalSliderTransitionSpeed
                }, 10)

                // Change Url
                _this._changeUrlHashdata($(this).attr("data-index"))
            }
        })
    },

    _getCurrentSlideId: (element) => $(element).attr("data-index"),

    /**
     * Wenn man auf dem Next/Previous clickt
     * @private
     */
    _eventNavArrowClicked: function () {
        $(this.config.rsNavNext).click(() => {
            this.slider.next()
        })

        $(this.config.rsNavPrevious).click(() => {
            this.slider.prev()
        })
    },

    /**
     * Schließe den Slider-Fullscreen-Modus
     *
     * @private
     */
    _eventNavCloseFullscreenClicked: function () {
        $(this.config.rsNavCloseFullcreen).click(() => {
            this.slider.exitFullscreen()

            if (this.config.noBannerOnGalleryClosed) {
                return
            }

            if (userData.hasBannerDisabled) {
                return
            }

            let mobileBodyTag = "ldsmobile"
            if (this.config.isMobileVersion) {
                mobileBodyTag = "view-mobilenew"
            }

            const bodymobile = $(`body.${mobileBodyTag}`)
            if (bodymobile) {
                if (userData.isAgeVerified) {
                    const str_append = `<div id="galleryInject"><div class="gallery-inject-container"><div style="padding-top: 20px;"><p class="gi-text">Anzeige</p></div><script async type="application/javascript" src="https://a.magsrv.com/ad-provider.js"></script><ins class="eas6a97888e" data-zoneid="5040782"></ins><script>(AdProvider = window.AdProvider || []).push({"serve": {}});</script><div style="padding-top: 10px;"><a href="#" onclick="$(\'#galleryInject\').remove(); return false;" class="button-sm ui-btn ui-shadow ui-btn-corner-all ui-btn-icon-left ui-btn-up-b"><span class="btn btn-lg btn-block btn-ladies-primary m-5-b ui-btn-inner ui-btn-corner-all"><span class="ui-btn-text">Werbung schließen</span></span></a></div>${siteConfig.bannerFreeGalleryLink}</div></div>`
                } else {
                    const str_append = `<div id="galleryInject"><div class="gallery-inject-container"><div style="padding-top: 20px;"><p class="gi-text">Anzeige</p></div><iframe src="https://syndication.realsrv.com/ads-iframe-display.php?idzone=3767095&size=300x250" width="300" height="250" scrolling="no" marginwidth="0" marginheight="0" frameborder="0"></iframe></iframe><div style="padding-top: 10px;"><a href="#" onclick="$(\'#galleryInject\').remove(); return false;" class="button-sm ui-btn ui-shadow ui-btn-corner-all ui-btn-icon-left ui-btn-up-b"><span class="btn btn-lg btn-block btn-ladies-primary m-5-b ui-btn-inner ui-btn-corner-all"><span class="ui-btn-text">Werbung schließen</span></span></a></div>${siteConfig.bannerFreeGalleryLink}</div></div>`
                }
                bodymobile.append(str_append)
            }
        })
    },

    _eventWindowResized: () => {
        // TODO Cowboy was replaced with lodash debounce
        // $(window).on("resize", Cowboy.debounce(100, this._hideGalleryThumbnailNavigation.bind(this)))
    },

    _hideGalleryThumbnailNavigation: function () {
        this.deviceHeight = this._getDeviceHeight()
        this.deviceWidth = this._getDeviceWidth()

        this._updateViewType()

        if (this.slider.isFullscreen) {
            if (this.deviceHeight < this.config.minHeightForThumbs) {
                $(this.config.domThumbs).hide()
                $(this.config.domOverflow).addClass("full-height")
                this.showThumbs = false
                this._domHideThumbNav()
            } else {
                $(this.config.domOverflow).removeClass("full-height")
                $(this.config.domThumbs).show()
                this.showThumbs = true
                this._domShowThumbNav()
            }

            if (this.config.showMobileView) {
                $(this.config.domSliderlId).addClass("mobile-slider")
            } else {
                $(this.config.domSliderlId).removeClass("mobile-slider")
            }
        }

        this.slider.updateSliderSize(true)
        this._repositionVideo()
    },

    /**
     * Wenn Image-Index in der Url, öffne den Slider mit diesem Bild automatisch
     */
    _reopenImageFromUrl: function () {
        // ==== Open Slider if hastring defined ==== //
        if (window.location.hash !== "") {
            const hashtags = LadiesMain._getHashtagObject()
            const key = this.config.domContainerId.split("#")[1]

            if (key in hashtags) {
                // schalte die Transition aus
                this.slider.st.transitionSpeed = 0

                this.currentSlideId = Number.parseInt(hashtags[key], 10)

                if (this.currentSlideId > this.slider.numSlides - this.anzeigeCount) {
                    this.currentSlideId = 0
                    this._changeUrlHashdata(this.currentSlideId)
                }

                // Elementenzahl hinzufügen
                this._domCreateCountContainer()

                this._domCreateNavContainer()

                // HOTFIX issues with the slider after solads ad
                // Maybe we have a better way to wait for solads before triggering the slider change here
                setTimeout(() => {
                    this.openSlider()

                    // gehe zum Slide
                    this.slider.goTo(this.currentSlideId)

                    // schalte die Transition wieder ein für nächste Slides
                    setTimeout(() => {
                        this.slider.updateSliderSize(true)
                        this.slider.st.transitionSpeed = this.config.royalSliderTransitionSpeed
                    }, 30)
                }, 100)
            }
        }
    },

    /**
     * Zeige die Galerie
     */
    openSlider: function () {
        this.sliderContainer.show()

        // Fullscreen-Modus aktivieren
        this.slider.enterFullscreen()

        if (true === this.isIos) {
            disableBodyScroll(true, this.config.allowScrollingInSelector)
        }

        this.sliderContainer.data("galleryActive", true)
        $(document).data("keyOverrideActive", true)

        this._hideGalleryThumbnailNavigation()
    },

    /**
     * Url-Hashdate ändern, damit nach dem Neuladen der Seite
     * die Galerie automatisch aufgemacht wird.
     */
    _changeUrlHashdata: function (index) {
        LadiesMain.addHashtagToUrl(this.config.domContainerId.substring(1), index)
    },

    /**
     * Initialisiere un zeige das HTML-Element für Anzahl der Bildern
     */
    _domCreateCountContainer: function () {
        $(this.config.domCountContainerId).insertBefore(this.config.royalSliderContainer)
        $(this.config.domCountContainerId).show()
    },

    /**
     * Initialisiere un zeige das HTML-Element für die Navigation
     */
    _domCreateNavContainer: function () {
        $(this.config.rsNavPrevious).insertAfter(this.config.royalSliderContainer)
        $(this.config.rsNavNext).insertAfter(this.config.royalSliderContainer)
        $(this.config.rsNavCloseFullcreen).insertAfter(this.config.royalSliderContainer)
        $(this.config.rsNavMediaTextMobile).insertAfter(this.config.royalSliderContainer)

        $(this.config.domSliderlId).append($(this.config.thumbArrowLeft))
        $(this.config.domSliderlId).append($(this.config.thumbArrowRight))
    },

    /**
     * Zeige die Thumbs, wenn Slider in FullScreeen-Modus
     * @private
     */
    _domShowThumbNav: function () {
        $(this.config.thumbArrowLeft).show()
        $(this.config.thumbArrowRight).show()
    },

    /**
     * Verstecke die Thumbs, wenn Slider geht aus dem FullScreeen-Modus
     * @private
     */
    _domHideThumbNav: function () {
        $(this.config.thumbArrowLeft).hide()
        $(this.config.thumbArrowRight).hide()
    },

    _domShowNavigation: function () {
        $(this.config.rsNavPrevious).fadeIn()
        $(this.config.rsNavNext).fadeIn()
        $(this.config.rsNavCloseFullcreen).fadeIn()
        $(this.config.domCountContainerId).fadeIn()

        if ($(window).width() < 768) {
            // $(this.config.rsNavMediaTextMobile).fadeIn();
        }

        this.navigationVisible = true
    },

    _domHideNavigation: function () {
        $(this.config.rsNavPrevious).fadeOut()
        $(this.config.rsNavNext).fadeOut()
        $(this.config.rsNavCloseFullcreen).fadeOut()
        // $(this.config.rsNavMediaTextMobile).fadeOut()
        $(this.config.domCountContainerId).fadeOut()

        this.navigationVisible = false
    },

    _getOS: () => {
        const userAgent = window.navigator.userAgent
        const platform = window.navigator.platform
        const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"]
        const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"]
        const iosPlatforms = ["iPhone", "iPad", "iPod"]
        let os = null

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = "Mac OS"
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = "iOS"
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = "Windows"
        } else if (/Android/.test(userAgent)) {
            os = "Android"
        } else if (!os && /Linux/.test(platform)) {
            os = "Linux"
        }

        return os
    },

    _registerVideoPlayers: function () {
        $(".flowplayer").each((index, flowPlayerElement) => {
            const playerElement = $(flowPlayerElement)
            const player = playerElement.data("flowplayer")

            if (!player) {
                playerElement.on("boot", this.flowPlayerEvents.bind(this))
            } else {
                this.flowPlayerEvents(player)
            }
        })
    },
}
